import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import documentGeneratorService from '../services/documentGeneratorService';
import apiService from '../services/apiService';
import { useUiLanguage } from '../context/UiLanguageContext';
import { Sostoyanie } from '../components/FitenkoUzor';
import {
  PageHeader,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Textarea,
  EmptyState,
  inputStyle,
  Label
} from '../components/ui';

const labelStyle = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '0.375rem'
};

const selectStyle = {
  ...inputStyle,
  display: 'block'
};

const DocumentGeneratorPage = () => {
  const { t } = useUiLanguage();
  const [templates, setTemplates] = useState([]);
  const [cases, setCases] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [editedBody, setEditedBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Load templates and cases on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [templatesData, casesData] = await Promise.all([
          documentGeneratorService.getTemplates(),
          apiService.getCases(),
        ]);
        setTemplates(templatesData || []);
        setCases(casesData || []);
      } catch (err) {
        console.error('[DocumentGenerator] initial load error:', err);
        toast.error(t('practice.docgen.load_failed'));
      } finally {
        setInitialLoad(false);
      }
    };
    loadData();
  }, [t]);

  const handleGenerate = async () => {
    if (!selectedTemplateId || !selectedCaseId) {
      toast.error(t('practice.docgen.select_both_prompt'));
      return;
    }
    setLoading(true);
    setGeneratedDoc(null);
    setEditedBody('');
    try {
      const doc = await documentGeneratorService.generateDocument(
        Number(selectedTemplateId),
        Number(selectedCaseId)
      );
      setGeneratedDoc(doc);
      setEditedBody(doc.body || '');
      toast.success(t('practice.docgen.generated_success'));
    } catch (err) {
      console.error('[DocumentGenerator] generateDocument error:', err);
      toast.error(t('practice.docgen.generate_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!generatedDoc) return;
    setSaving(true);
    try {
      const updated = await documentGeneratorService.updateDocument(generatedDoc.id, editedBody, false);
      setGeneratedDoc(updated);
      toast.success(t('practice.docgen.saved_success'));
    } catch (err) {
      console.error('[DocumentGenerator] updateDocument error:', err);
      toast.error(t('practice.docgen.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizeAndDownload = async () => {
    if (!generatedDoc) return;
    setSaving(true);
    try {
      await documentGeneratorService.updateDocument(generatedDoc.id, editedBody, true);
      toast.success(t('practice.docgen.finalized_toast'));
      window.open(documentGeneratorService.getExportUrl(generatedDoc.id), '_blank');
    } catch (err) {
      console.error('[DocumentGenerator] finalize/export error:', err);
      toast.error(t('practice.docgen.finalize_failed'));
    } finally {
      setSaving(false);
    }
  };

  if (initialLoad) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-border border-t-primary" />
        <span className="ml-3.5"><Sostoyanie tekst={t('practice.docgen.loading')} /></span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <PageHeader
        title={t('practice.docgen.title')}
        description={t('practice.docgen.subtitle')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 items-start">
        {/* Left Panel — Controls */}
        <Card>
          <CardHeader>
            <CardTitle>{t('practice.docgen.generate_heading')}</CardTitle>
          </CardHeader>
          <CardContent style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <Label htmlFor="template-select" style={labelStyle}>
                {t('practice.docgen.template_label')}
              </Label>
              <select
                id="template-select"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                style={selectStyle}
              >
                <option value="">{t('practice.docgen.template_placeholder')}</option>
                {templates.map((tmpl) => (
                  <option key={tmpl.id} value={tmpl.id}>
                    {tmpl.name} ({tmpl.doc_type})
                  </option>
                ))}
              </select>
              {templates.length === 0 && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  {t('practice.docgen.no_templates')}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="case-select" style={labelStyle}>
                {t('practice.docgen.case_label')}
              </Label>
              <select
                id="case-select"
                value={selectedCaseId}
                onChange={(e) => setSelectedCaseId(e.target.value)}
                style={selectStyle}
              >
                <option value="">{t('practice.docgen.case_placeholder')}</option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.case_number} — {c.title || c.case_type}
                  </option>
                ))}
              </select>
            </div>

            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={loading || !selectedTemplateId || !selectedCaseId}
              loading={loading}
              style={{ width: '100%', marginTop: '0.25rem' }}
            >
              {loading ? t('practice.docgen.generating') : t('practice.docgen.generate_btn')}
            </Button>

            {generatedDoc && (
              <div style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
                  {t('practice.docgen.doc_id')}{' '}
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent-ink)' }}>
                    #{generatedDoc.id}
                  </span>
                </p>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{t('practice.docgen.status')}</span>
                  <Badge variant={generatedDoc.is_finalized ? 'success' : 'secondary'}>
                    {generatedDoc.is_finalized ? t('practice.docgen.finalized') : t('practice.docgen.draft')}
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Panel — Editor */}
        <div>
          {generatedDoc ? (
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full">
                  <CardTitle>{t('practice.docgen.generated_heading')}</CardTitle>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSave}
                      disabled={saving}
                      loading={saving}
                      className="w-full sm:w-auto whitespace-nowrap"
                    >
                      {saving ? t('practice.docgen.saving') : t('practice.docgen.save_draft')}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleFinalizeAndDownload}
                      disabled={saving}
                      className="w-full sm:w-auto whitespace-nowrap"
                    >
                      {t('practice.docgen.finalize_pdf')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  rows={20}
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  placeholder={t('practice.docgen.empty_editor')}
                  data-user-content="true"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: '0.875rem' }}
                />
              </CardContent>
            </Card>
          ) : (
            <Card style={{ minHeight: '420px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CardContent style={{ padding: '2.5rem', width: '100%' }}>
                <EmptyState
                  variant="no-data"
                  style={{ border: 'none', backgroundColor: 'transparent' }}
                  title={loading ? t('practice.docgen.generating') : t('practice.docgen.empty_editor')}
                  description={t('practice.docgen.subtitle')}
                  icon={
                    <div style={{
                      width: '3.5rem',
                      height: '3.5rem',
                      borderRadius: '50%',
                      backgroundColor: 'var(--accent-soft)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid var(--accent-border)'
                    }}>
                      <svg width="24" height="24" fill="none" stroke="var(--accent-ink)" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  }
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentGeneratorPage;
